import React, { useEffect, useState } from 'react';
import { Box, Card, CardContent, Typography, Button, Grid, Container, CircularProgress, LinearProgress, Chip, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { useSelector } from 'react-redux';
import invoiceIntegratorService from "../services/invoiceIntegrator";
import marketplacesService from '../services/marketplacesService';
import ecommercePlaceService from '../services/ecommercePlaceService';
import specialApplicationService from '../services/specialApplicationService';
import userApplicationService from '../services/userApplicationService';
import Tooltip from '@mui/material/Tooltip';
import { format, differenceInDays } from 'date-fns';
import { tr } from 'date-fns/locale';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';

// Entegrasyon kartı bileşeni
const IntegrationCard = ({ 
  id, 
  name, 
  icon, 
  originalPrice, 
  discountedPrice, 
  period, 
  description, 
  type, // 'marketplace', 'ecommerce', 'invoice', 'specialApplication'
  subscriptionInfo, // Abonelik bilgisi
  onRequestTrial, // 14 gün deneme talep et
  onPurchase // Satın alma
}) => {
  const [openDialog, setOpenDialog] = useState(false);
  const navigate = useNavigate();

  const handlePurchaseClick = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleConfirmPurchase = () => {
    onPurchase(id, type);
    handleCloseDialog();
    navigate('/user-tasks');
  };

  const hasSubscription = subscriptionInfo && subscriptionInfo.id;
  const isActive = hasSubscription && new Date(subscriptionInfo.validityDate) > new Date();
  const trialEnded = hasSubscription && new Date(subscriptionInfo.validityDate) <= new Date();
  
  // Kalan gün hesaplama
  const daysLeft = isActive ? 
    differenceInDays(new Date(subscriptionInfo.validityDate), new Date()) : 0;
  
  // Abonelik yüzdesini hesaplama (14 gün üzerinden)
  const subscriptionPercentage = isActive ? 
    Math.max(0, Math.min(100, (daysLeft / 14) * 100)) : 0;
    
  // Tarih formatı
  const formatDate = (dateString) => {
    return format(new Date(dateString), 'dd MMMM yyyy', { locale: tr });
  };

  return (
    <>
      <Tooltip title={description || name} arrow>
        <Card sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          padding: 3, 
          height: '100%',
          boxShadow: '0px 2px 4px rgba(0,0,0,0.1)',
          borderRadius: 2,
          position: 'relative'
        }}>
          {isActive && (
            <Box sx={{ position: 'absolute', top: 0, right: 0, m: 1 }}>
              <Chip 
                label={`${daysLeft} gün kaldı`} 
                color="success" 
                size="small" 
                sx={{ fontWeight: 'bold' }}
              />
            </Box>
          )}
          
          {trialEnded && (
            <Box sx={{ position: 'absolute', top: 0, right: 0, m: 1 }}>
              <Chip 
                label="Süre doldu" 
                color="error" 
                size="small" 
                sx={{ fontWeight: 'bold' }}
              />
            </Box>
          )}
          
          <Typography variant="h6" component="div" sx={{ mb: 3, textAlign: 'center' }}>
            {name}
          </Typography>
       
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center', alignItems: 'center', height: 80 }}>
            <img src={icon} alt={name} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
          </Box>
          
          {isActive && (
            <Box sx={{ width: '100%', mt: 2, mb: 1 }}>
              <LinearProgress 
                variant="determinate" 
                value={subscriptionPercentage} 
                sx={{ 
                  height: 8, 
                  borderRadius: 5,
                  backgroundColor: '#e0e0e0',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: daysLeft <= 3 ? '#ff5252' : daysLeft <= 7 ? '#ffab40' : '#4caf50',
                    borderRadius: 5
                  }
                }}
              />
              <Typography variant="caption" sx={{ display: 'block', mt: 0.5, textAlign: 'center' }}>
                Bitiş tarihi: {formatDate(subscriptionInfo.validityDate)}
              </Typography>
            </Box>
          )}
          
          <Box sx={{ mt: 'auto', width: '100%', textAlign: 'center' }}>
            {!isActive && !trialEnded && (
              <Button 
                variant="contained" 
                fullWidth 
                onClick={() => onRequestTrial(id, type)}
                sx={{ 
                  mt: 2, 
                  backgroundColor: '#1976d2', 
                  '&:hover': { backgroundColor: '#1565c0' } 
                }}
              >
                14 Gün Dene
              </Button>
            )}
            
            {trialEnded && (
              <Button 
                variant="contained" 
                fullWidth 
                onClick={handlePurchaseClick}
                sx={{ 
                  mt: 2, 
                  backgroundColor: '#f57c00', 
                  '&:hover': { backgroundColor: '#ef6c00' } 
                }}
              >
                Satın Al
              </Button>
            )}
            
            {isActive && (
              <Button 
                variant="outlined" 
                fullWidth 
                onClick={handlePurchaseClick}
                sx={{ 
                  mt: 2
                }}
              >
                Yenile
              </Button>
            )}
          </Box>
        </Card>
      </Tooltip>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        aria-labelledby="purchase-dialog-title"
        aria-describedby="purchase-dialog-description"
      >
        <DialogTitle id="purchase-dialog-title">
          Satın Alma Onayı
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" id="purchase-dialog-description">
            Seçtiğiniz pazaryeri entegrasyonunu satın almak üzere bir talep oluşturulacaktır. Bu işlem sonrasında ekibimiz en kısa sürede sizinle iletişime geçerek süreci başlatacaktır.
          </Typography>
          <Typography variant="body1" sx={{ mt: 2, fontWeight: 'bold' }}>
            Devam etmek istiyor musunuz?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            İptal
          </Button>
          <Button onClick={handleConfirmPurchase} color="primary" variant="contained" autoFocus>
            Talep Oluştur
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

IntegrationCard.propTypes = {
  id: PropTypes.number.isRequired,
  name: PropTypes.string.isRequired,
  icon: PropTypes.string.isRequired,
  originalPrice: PropTypes.string,
  discountedPrice: PropTypes.string,
  period: PropTypes.string,
  description: PropTypes.string,
  type: PropTypes.oneOf(['marketplace', 'ecommerce', 'invoice', 'specialApplication']).isRequired,
  subscriptionInfo: PropTypes.shape({
    id: PropTypes.number,
    validityDate: PropTypes.string
  }),
  onRequestTrial: PropTypes.func.isRequired,
  onPurchase: PropTypes.func.isRequired
};

export const ApplicationPage = () => {
  const navigate = useNavigate();
  const [integrations, setIntegrations] = useState([]);
  const [marketplaces, setMarketPlace] = useState([]);
  const [ecommercePlaces, setEcommercePlace] = useState([]);
  const [specialApplications, setSpecialApplication] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userApplications, setUserApplications] = useState([]);
  const [loadingUserData, setLoadingUserData] = useState(true);
  
  const userId = useSelector(state => state?.auth?.user?.id);

  // Kullanıcı aboneliklerini getir
  const getUserApplications = async () => {
    try {
      setLoadingUserData(true);
      const response = await userApplicationService.fetchUserApplications();
      
      if (response && response.status && response.data && response.data.data) {
        setUserApplications(response.data.data);
      }
    } catch (error) {
      console.error("Kullanıcı abonelikleri alınırken hata:", error);
      toast.error("Kullanıcı abonelik bilgileri alınamadı.");
    } finally {
      setLoadingUserData(false);
    }
  };

  // Fatura entegrasyonlarını getir
  const getInvoiceIntegrator = async () => {
    try {
      const response = await invoiceIntegratorService.getInvoiceIntegratorApiCC();
      
      if (response && response.data && response.data.data) {
        const transformedData = response.data.data.data.map(item => ({
          id: item.id,
          name: item.name,
          icon: item.iconLink,
          originalPrice: "1200", // Örnek fiyatlar 
          discountedPrice: "999",
          period: "Yıllık",
          description: item.description || ""
        }));
        
        setIntegrations(transformedData);
      } 
    } catch (ex) {
      console.error("Fatura entegrasyonları alınırken hata:", ex);
    }
  };

  // Pazaryeri entegrasyonlarını getir
  const getMarketPlaces = async () => {
    try {
      const response = await marketplacesService.getMarketPlcesForCC();
  
      if (response && response.data && response.data.data) {
        const transformedData = response.data.data.data.map(item => ({
          id: item.id,
          name: item.name,
          icon: item.iconLink,
          originalPrice: "1500", // Örnek fiyatlar
          discountedPrice: "1200",
          period: "Yıllık",
          description: item.description || ""
        }));  
        setMarketPlace(transformedData);
      }
    } catch (error) {
      console.error("Pazaryeri entegrasyonları alınırken hata:", error);
    }
  };

  // E-ticaret entegrasyonlarını getir
  const getEcommercePlaces = async () => {
    try {
      const response = await ecommercePlaceService.getEcommercePlacesForCC();
  
      if (response && response.data && response.data) {
        const transformedData = response.data.data.data.map(item => ({
          id: item.id,
          name: item.name,
          icon: item.iconLink,
          originalPrice: "1300", // Örnek fiyatlar
          discountedPrice: "1100",
          period: "Yıllık",
          description: item.description || ""
        }));
        setEcommercePlace(transformedData);
      }
    } catch (error) {
      console.error("E-ticaret entegrasyonları alınırken hata:", error);
    }
  };

  // Özel uygulamaları getir
  const getSpecialApplication = async () => {
    try {
      const response = await specialApplicationService.getSpecialApplication();
    
      if (response && response.data && response.data) {
        const transformedData = response.data.data.data.map(item => ({
          id: item.id,
          name: item.name,
          icon: item.iconLink,
          originalPrice: "1000", // Örnek fiyatlar
          discountedPrice: "800",
          period: "Yıllık",
          description: item.description || ""
        }));
        setSpecialApplication(transformedData);
      }
    } catch (error) {
      console.error("Özel uygulamalar alınırken hata:", error);
    }
  };

  // 14 günlük deneme talebi
  const handleRequestTrial = async (id, type) => {
    try {
      // Abonelik tipi için gerekli parametreleri hazırla
      let applicationData = {
        userId: userId
      };
      
      // Entegrasyon tipine göre request verisi oluştur
      switch (type) {
        case 'marketplace':
          applicationData.marketPlaceId = id;
          break;
        case 'ecommerce':
          applicationData.ecommercePlaceId = id;
          break;
        case 'invoice':
          applicationData.invoicePlaceId = id;
          break;
        case 'specialApplication':
          applicationData.specialApplicationId = id;
          break;
        default:
          throw new Error("Geçersiz entegrasyon tipi");
      }
      
      // Validatydate'i 14 gün sonrasına ayarla
      const today = new Date();
      const validityDate = new Date(today);
      validityDate.setDate(today.getDate() + 14);
      
      applicationData.validityDate = validityDate.toISOString();
      
      // Abonelik oluştur
      const response = await userApplicationService.createTrialApplication(applicationData);
      
      if (response && response.status) {
        toast.success("14 günlük deneme sürümü başarıyla etkinleştirildi.\nLütfen çıkış yapıp giriş yapın!");
        getUserApplications(); // Kullanıcı aboneliklerini güncelle
      } else {
        toast.error("Deneme sürümü etkinleştirilemedi.");
      }
    } catch (error) {
      console.error("Deneme talebi oluşturulurken hata:", error);
      toast.error("Deneme sürümü etkinleştirilemedi: " + (error.message || "Bilinmeyen hata"));
    }
  };

  // Satın alma
  const handlePurchase = (id, type) => {
    // Ödeme sayfasına yönlendir
    let paymentType = '';
    
    switch (type) {
      case 'marketplace':
        paymentType = 'marketPlace';
        break;
      case 'ecommerce':
        paymentType = 'ecommerce';
        break;
      case 'invoice':
        paymentType = 'invoice';
        break;
      case 'specialApplication':
        paymentType = 'specialApp';
        break;
      default:
        paymentType = 'unknown';
    }
    
    navigate(`/payment/${paymentType}/${id}`);
  };

  // Kullanıcı aboneliğini bul
  const findUserSubscription = (id, type) => {
    if (!userApplications || userApplications.length === 0) return null;
    
    let subscription = null;
    
    switch (type) {
      case 'marketplace':
        subscription = userApplications.find(app => app.marketPlaceId === id);
        break;
      case 'ecommerce':
        subscription = userApplications.find(app => app.ecommercePlaceId === id);
        break;
      case 'invoice':
        subscription = userApplications.find(app => app.invoicePlaceId === id);
        break;
      case 'specialApplication':
        subscription = userApplications.find(app => app.specialApplicationId === id);
        break;
      default:
        subscription = null;
    }
    
    return subscription;
  };

  useEffect(() => {
    // Tüm verileri yükleme
    const loadAllData = async () => {
      setLoading(true);
      await Promise.all([
        getInvoiceIntegrator(),
        getMarketPlaces(),
        getEcommercePlaces(),
        getSpecialApplication(),
        getUserApplications()
      ]);
      setLoading(false);
    };
    
    loadAllData();
  }, []);

  if (loading || loadingUserData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: '#f0f7ff', minHeight: '100vh', py: 4 }}>
      <Container maxWidth="lg">
        <Typography variant="h4" component="h1" fontWeight="bold" color="text.primary" sx={{ mb: 1 }}>
          Fatura Entegrasyonları
        </Typography>        
        <Grid container spacing={3}>
          {integrations.map(integration => (
            <Grid item xs={12} sm={6} md={4} key={integration.id}>
              <IntegrationCard
                id={integration.id}
                name={integration.name}
                icon={integration.icon}
                originalPrice={integration.originalPrice}
                discountedPrice={integration.discountedPrice}
                period={integration.period}
                description={integration.description}
                type="invoice"
                subscriptionInfo={findUserSubscription(integration.id, 'invoice')}
                onRequestTrial={handleRequestTrial}
                onPurchase={handlePurchase}
              />
            </Grid>
          ))}
        </Grid>
        <br/> 
        <Typography variant="h4" component="h1" fontWeight="bold" color="text.primary" sx={{ mb: 1 }}>
          Pazaryeri Entegrasyonları
        </Typography>        
        <Grid container spacing={3}>
          {marketplaces.map(marketPlace => (
            <Grid item xs={12} sm={6} md={4} key={marketPlace.id}>
              <IntegrationCard
                id={marketPlace.id}
                name={marketPlace.name}
                icon={marketPlace.icon}
                originalPrice={marketPlace.originalPrice}
                discountedPrice={marketPlace.discountedPrice}
                period={marketPlace.period}
                description={marketPlace.description}
                type="marketplace"
                subscriptionInfo={findUserSubscription(marketPlace.id, 'marketplace')}
                onRequestTrial={handleRequestTrial}
                onPurchase={handlePurchase}
              />
            </Grid>
          ))}
        </Grid>
        <br/> 
        <Typography variant="h4" component="h1" fontWeight="bold" color="text.primary" sx={{ mb: 1 }}>
          E-Ticaret Entegrasyonları
        </Typography>        
        <Grid container spacing={3}>
          {ecommercePlaces.map(ecommerce => (
            <Grid item xs={12} sm={6} md={4} key={ecommerce.id}>
              <IntegrationCard
                id={ecommerce.id}
                name={ecommerce.name}
                icon={ecommerce.icon}
                originalPrice={ecommerce.originalPrice}
                discountedPrice={ecommerce.discountedPrice}
                period={ecommerce.period}
                description={ecommerce.description}
                type="ecommerce"
                subscriptionInfo={findUserSubscription(ecommerce.id, 'ecommerce')}
                onRequestTrial={handleRequestTrial}
                onPurchase={handlePurchase}
              />
            </Grid>
          ))}
        </Grid>
        
        <br/> 
        <Typography variant="h4" component="h1" fontWeight="bold" color="text.primary" sx={{ mb: 1 }}>
          Özel Uygulamalar
        </Typography>        
        <Grid container spacing={3}>
          {specialApplications.map(specialApp => (
            <Grid item xs={12} sm={6} md={4} key={specialApp.id}>
              <IntegrationCard
                id={specialApp.id}
                name={specialApp.name}
                icon={specialApp.icon}
                originalPrice={specialApp.originalPrice}
                discountedPrice={specialApp.discountedPrice}
                period={specialApp.period}
                description={specialApp.description}
                type="specialApplication"
                subscriptionInfo={findUserSubscription(specialApp.id, 'specialApplication')}
                onRequestTrial={handleRequestTrial}
                onPurchase={handlePurchase}
              />
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};